import { useQuery } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

import { usePageTitle } from '../../hooks/usePageTitle'
import { Booking } from '../../models/Booking'
import { Campaign } from '../../models/Campaign'
import { Client } from '../../models/Client'
import { Estimate } from '../../models/Estimate'
import { Gift } from '../../models/Gift'
import { Invoice } from '../../models/Invoice'
import { Item } from '../../models/Item'
import { Lesson } from '../../models/Lesson'
import { Order } from '../../models/Order'
import { PSRecord } from '../../models/PSRecord'
import { Payee } from '../../models/Payee'
import { RecordDataMap } from '../../models/RecordDataMap'
import { RecordFactory } from '../../models/RecordFactory'
import { Signature } from '../../models/Signature'
import { Task } from '../../models/Task'
import { Trigger } from '../../models/Trigger'
import { RecordNotFoundError } from '../../models/errors/RecordNotFoundError'
import { getTransactionDocument } from '../../utils/transaction'
import { ComponentData } from '../../utils/typing'
import { IconNameAndCategoryString } from '../Icon'
import { PocketComponent, PocketComponentProps, usePSComponent } from '../PocketComponent'
import { ListHeader } from '../common/ListHeader'
import { BillingsDashboard } from './BillingsDashboards'
import { BookingsDashboard } from './BookingsDashboard'
import { CancellationsDashboard } from './CancellationsDashboard'
import { ClassesDashboard } from './ClassesDashboard'
import { ClientsDashboard } from './ClientsDashboard'
import { CompleteDashboard } from './CompleteDashboard'
import { ContractsDashboard } from './ContractsDashboard'
import { DepositsDashboard } from './DepositsDashboard'
import { EmployeesDashboard } from './EmployeesDashboard'
import { EstimatesDashboard } from './EstimatesDashboard'
import { ExpensesDashboard } from './ExpensesDashboard'
import { GiftsDashboard } from './GiftsDashboard'
import { InventoryDashboard } from './InventoryDashboard'
import { InvoicesDashboard } from './InvoicesDashboard'
import { ItemsDashboard } from './ItemsDashboard'
import { LinksDashboard } from './LinksDashboard'
import { MessagesDashboard } from './MessagesDashboard'
import { OrdersDashboard } from './OrdersDashboard'
import { PackagesDashboard } from './PackagesDashboard'
import { PendingDashboard } from './PendingDashboard'
import { RecordsDashboard } from './RecordsDashboard'
import { ReservationsDashboard } from './ReservationsDashboard'
import { SubscriptionsDashboard } from './SubscriptionsDashboard'
import { TasksDashboard } from './TasksDashboard'
import { TriggersDashboard } from './TriggersDashboard'
import { useDashboardBack } from './useDashboardBack'

const viewWillAppear = async (base: PocketComponent) => {
    const clientId = base.searchParams.get('clientId') ?? undefined
    const subaccountId = base.searchParams.get('subaccountId') ?? undefined
    const dashboardId = base.urlParams ? base.urlParams.dashboard : null
    const dashboard = await RecordFactory.getInstance().loadDashboard(dashboardId)
    if (!dashboard) {
        throw new RecordNotFoundError('Dashboard not found')
    }
    return { dashboard, clientId, subaccountId }
}

const getActionButtonIcon = (dashboardId: string | undefined): IconNameAndCategoryString => {
    if (dashboardId === 'deposits') {
        return 'dollar3.small'
    }
    if (dashboardId === 'links') {
        return 'pencil.small'
    }
    return 'plus.small'
}

const render = ({ data, base }: PocketComponentProps<ComponentData<typeof viewWillAppear>>) => {
    if (data instanceof RecordNotFoundError) {
        throw data
    }

    const onDashboardBack = useDashboardBack()
    const [searchParams, setSeachParams] = useSearchParams()
    const [searchText, setSearchText] = useState('')
    const [cancelledBookings, setCancelledBookings] = useState<Booking[]>([])

    const viewFilter = Number(searchParams.get('viewFilter') ?? 0)
    const dateFilter =
        searchParams.get('dateFilter') ??
        data.dashboard?.getDefaultDateTypeOption(viewFilter)?.value ??
        'all'

    const dashboard = data.dashboard
    const { data: fetchedDashboard, isLoading } = useQuery({
        queryKey: ['dashboard', data.dashboard?.id, data.clientId, viewFilter, dateFilter],
        queryFn: async () => {
            const result = await RecordFactory.getInstance().fetchDashboard(
                data.dashboard?.id!,
                data.clientId,
                data.subaccountId,
                viewFilter,
                dateFilter
            )

            if (!result.ok || !result.value) {
                throw new RecordNotFoundError('Dashboard not found')
            }

            return result.value
        },
    })

    const showSearchBar = !['deposits'].includes(dashboard.id ?? '')
    const showDateFilter = !['employees', 'triggers'].includes(dashboard.id ?? '')
    const showViewFilter = dashboard.viewOptions.length > 1
    const showActionButton = dashboard.buttonString && dashboard.buttonLinkUrl
    // const showSecondaryActionButton = dashboard.moreButtonMessage && dashboard.moreButtonTitle

    const title = dashboard?.title ?? dashboard?.id
    usePageTitle(title)

    // not all dashboards have the same data structure, so we need to filter the results based on the dashboard id
    const filteredResults = useMemo(() => {
        if (dashboard.id === 'orders') {
            return (
                fetchedDashboard?.results?.filter((o: Order) =>
                    o.person?.name?.toLowerCase().includes(searchText.toLowerCase())
                ) ?? []
            )
        }

        if (dashboard.id === 'messages') {
            return (
                fetchedDashboard?.results?.filter(
                    (r: Campaign) =>
                        r.person?.name?.toLowerCase().includes(searchText.toLowerCase()) ||
                        r.template?.title?.toLowerCase().includes(searchText.toLowerCase())
                ) ?? []
            )
        }

        if (dashboard.id === 'inventory') {
            return (
                fetchedDashboard?.results?.filter((i: Item) =>
                    i.name?.toLowerCase().includes(searchText.toLowerCase())
                ) ?? []
            )
        }

        if (dashboard.id === 'clients') {
            return (
                fetchedDashboard?.results?.filter((c: Client) =>
                    c.person?.name?.toLowerCase().includes(searchText.toLowerCase())
                ) ?? []
            )
        }

        if (dashboard.id === 'tasks') {
            return (
                fetchedDashboard?.results?.filter(
                    (r: Task) =>
                        r.title?.toLowerCase().includes(searchText.toLowerCase()) ||
                        r.person?.name?.toLowerCase().includes(searchText.toLowerCase())
                ) ?? []
            )
        }

        if (dashboard.id === 'items') {
            return (
                fetchedDashboard?.results?.filter((r: RecordDataMap) => {
                    const item = r.valueForRecordKey<Item>('item', Item)
                    const tranDoc = getTransactionDocument(r)
                    return (
                        item?.name?.toLowerCase().includes(searchText.toLowerCase()) ||
                        tranDoc?.client?.name?.toLowerCase().includes(searchText.toLowerCase())
                    )
                }) ?? []
            )
        }

        if (dashboard.id === 'triggers') {
            return (
                fetchedDashboard?.results?.filter((r: RecordDataMap) =>
                    r
                        .valueForRecordKey<Trigger>('trigger', Trigger)
                        ?.label?.toLowerCase()
                        .includes(searchText.toLowerCase())
                ) ?? []
            )
        }

        if (dashboard.id === 'contracts') {
            return (
                fetchedDashboard?.results?.filter((c: Signature) =>
                    c.contract?.name?.toLowerCase().includes(searchText.toLowerCase())
                ) ?? []
            )
        }

        if (dashboard.id === 'classes') {
            return (
                fetchedDashboard?.results?.filter(
                    (c: Lesson) =>
                        !c.item || c.item?.name?.toLowerCase().includes(searchText.toLowerCase())
                ) ?? []
            )
        }

        if (dashboard.id === 'links') {
            if (!searchText) {
                return fetchedDashboard?.results ?? []
            }

            return (
                fetchedDashboard?.results?.filter(
                    (r: RecordDataMap) =>
                        (r.valueForKey('referer') ?? 'Unknown')
                            .toLowerCase()
                            .includes(searchText.toLowerCase()) ||
                        (r.valueForKey('link') ?? 'Unknown')
                            .toLowerCase()
                            .includes(searchText.toLowerCase())
                ) ?? []
            )
        }

        if (dashboard.id === 'records') {
            return (
                fetchedDashboard?.results?.filter(
                    (r: PSRecord) =>
                        r.person?.name?.toLowerCase().includes(searchText.toLowerCase()) ||
                        r.type?.name?.toLowerCase().includes(searchText.toLowerCase())
                ) ?? []
            )
        }

        if (dashboard.id === 'invoices') {
            return (
                fetchedDashboard?.results?.filter(
                    (i: Invoice) =>
                        i.client?.name?.toLowerCase().includes(searchText.toLowerCase()) ||
                        i.number?.toString().includes(searchText.toLowerCase())
                ) ?? []
            )
        }

        if (dashboard.id === 'estimates') {
            return (
                fetchedDashboard?.results?.filter(
                    (e: Estimate) =>
                        e.client?.name?.toLowerCase().includes(searchText.toLowerCase()) ||
                        e.number?.toString().includes(searchText.toLowerCase())
                ) ?? []
            )
        }

        if (dashboard.id === 'employees') {
            return (
                fetchedDashboard?.results?.filter((r: Payee) =>
                    r.person?.name?.toLowerCase().includes(searchText.toLowerCase())
                ) ?? []
            )
        }

        if (dashboard.id === 'gifts') {
            return (
                fetchedDashboard?.results?.filter((r: Gift) =>
                    r.customer?.name?.toLowerCase().includes(searchText.toLowerCase())
                ) ?? []
            )
        }

        return (
            fetchedDashboard?.results?.filter(
                (r: any) =>
                    r.client?.name?.toLowerCase().includes(searchText.toLowerCase()) ||
                    r.subaccount?.name?.toLowerCase().includes(searchText.toLowerCase()) ||
                    r.services?.some((s: any) =>
                        s.name?.toLowerCase().includes(searchText.toLowerCase())
                    )
            ) ?? []
        )
    }, [dashboard, fetchedDashboard, searchText])

    return (
        <div className="relative h-screen w-full overflow-y-auto p-8 pt-6">
            {/* header */}

            <ListHeader
                title={title}
                backButton={onDashboardBack}
                searchText={searchText}
                onSearchTextChange={showSearchBar ? setSearchText : undefined}
                dateFilter={
                    showDateFilter
                        ? {
                              selectedDateFilter: dateFilter,
                              onDateFilterChange: (dateFilter) => {
                                  setSeachParams((s) => {
                                      s.set('dateFilter', dateFilter)
                                      return s
                                  })
                                  base.reload()
                              },
                              dateTypeOptions: dashboard?.getDateTypeOptions(viewFilter) ?? [],
                          }
                        : undefined
                }
                viewFilter={
                    showViewFilter
                        ? {
                              selectedViewFilter: viewFilter,
                              onViewFilterChange: (viewFilter) => {
                                  setSeachParams((s) => {
                                      s.set('viewFilter', viewFilter)
                                      return s
                                  })
                              },
                              viewOptions: dashboard?.viewOptions ?? [],
                          }
                        : undefined
                }
                actionButton={
                    showActionButton
                        ? {
                              icon: getActionButtonIcon(dashboard.id),
                              href: dashboard.buttonLinkUrl,
                              intent: 'primary',
                              label: dashboard.buttonString,
                          }
                        : undefined
                }
            />

            <div className="mt-6 rounded-lg bg-white p-8">
                {dashboard?.id === 'bookings' && (
                    <BookingsDashboard
                        bookings={filteredResults?.filter(
                            (b: Booking) => !cancelledBookings.includes(b)
                        )}
                        dashboard={fetchedDashboard}
                        onCancelBooking={(b) => {
                            setCancelledBookings([...cancelledBookings, b])
                        }}
                        searchText={searchText}
                        isLoading={isLoading}
                    />
                )}

                {dashboard?.id === 'complete' && (
                    <CompleteDashboard
                        bookings={filteredResults}
                        dashboard={fetchedDashboard}
                        searchText={searchText}
                        isLoading={isLoading}
                    />
                )}

                {dashboard?.id === 'pending' && (
                    <PendingDashboard
                        bookings={filteredResults}
                        dashboard={fetchedDashboard}
                        searchText={searchText}
                        isLoading={isLoading}
                    />
                )}

                {dashboard?.id === 'clients' && (
                    <ClientsDashboard
                        clients={filteredResults}
                        view={viewFilter}
                        dashboard={fetchedDashboard}
                        searchText={searchText}
                        isLoading={isLoading}
                    />
                )}

                {dashboard?.id === 'packages' && (
                    <PackagesDashboard
                        packages={filteredResults}
                        view={viewFilter}
                        dashboard={fetchedDashboard}
                        searchText={searchText}
                        isLoading={isLoading}
                    />
                )}

                {dashboard?.id === 'deposits' && (
                    <DepositsDashboard
                        deposits={fetchedDashboard?.results ?? []}
                        dashboard={fetchedDashboard}
                        searchText={searchText}
                        isLoading={isLoading}
                    />
                )}

                {dashboard?.id === 'expenses' && (
                    <ExpensesDashboard
                        payments={filteredResults}
                        dashboard={fetchedDashboard}
                        view={viewFilter}
                        searchText={searchText}
                        isLoading={isLoading}
                    />
                )}

                {dashboard?.id === 'employees' && (
                    <EmployeesDashboard
                        employees={filteredResults}
                        dashboard={fetchedDashboard}
                        searchText={searchText}
                        isLoading={isLoading}
                    />
                )}

                {dashboard?.id === 'subscriptions' && (
                    <SubscriptionsDashboard
                        subscriptions={filteredResults}
                        view={viewFilter}
                        dashboard={fetchedDashboard}
                        searchText={searchText}
                        isLoading={isLoading}
                    />
                )}

                {dashboard?.id === 'links' && (
                    <LinksDashboard
                        links={filteredResults}
                        view={viewFilter}
                        dashboard={fetchedDashboard}
                        searchText={searchText}
                        isLoading={isLoading}
                    />
                )}

                {dashboard?.id === 'records' && (
                    <RecordsDashboard
                        records={filteredResults}
                        view={viewFilter}
                        dashboard={fetchedDashboard}
                        searchText={searchText}
                        isLoading={isLoading}
                    />
                )}

                {dashboard?.id === 'classes' && (
                    <ClassesDashboard
                        classes={filteredResults}
                        view={viewFilter}
                        dashboard={fetchedDashboard}
                        searchText={searchText}
                        isLoading={isLoading}
                    />
                )}

                {dashboard?.id === 'reservations' && (
                    <ReservationsDashboard
                        dashboard={fetchedDashboard}
                        bookings={filteredResults}
                        searchText={searchText}
                        isLoading={isLoading}
                        viewFilter={viewFilter}
                    />
                )}

                {dashboard?.id === 'contracts' && (
                    <ContractsDashboard
                        contracts={filteredResults}
                        view={viewFilter}
                        dashboard={fetchedDashboard}
                        searchText={searchText}
                        isLoading={isLoading}
                    />
                )}

                {dashboard?.id === 'estimates' && (
                    <EstimatesDashboard
                        estimates={filteredResults}
                        dashboard={fetchedDashboard}
                        searchText={searchText}
                        isLoading={isLoading}
                    />
                )}

                {dashboard?.id === 'triggers' && (
                    <TriggersDashboard
                        triggers={filteredResults}
                        view={viewFilter}
                        dashboard={fetchedDashboard}
                        searchText={searchText}
                        isLoading={isLoading}
                    />
                )}

                {dashboard?.id === 'items' && (
                    <ItemsDashboard
                        items={filteredResults}
                        view={viewFilter}
                        dashboard={fetchedDashboard}
                        searchText={searchText}
                        isLoading={isLoading}
                    />
                )}

                {dashboard?.id === 'tasks' && (
                    <TasksDashboard
                        tasks={filteredResults}
                        view={viewFilter}
                        dashboard={fetchedDashboard}
                        searchText={searchText}
                        isLoading={isLoading}
                    />
                )}

                {dashboard?.id === 'cancellations' && (
                    <CancellationsDashboard
                        bookings={filteredResults}
                        view={viewFilter}
                        dashboard={fetchedDashboard}
                        searchText={searchText}
                        isLoading={isLoading}
                    />
                )}

                {dashboard?.id === 'gifts' && (
                    <GiftsDashboard
                        gifts={filteredResults}
                        view={viewFilter}
                        dashboard={fetchedDashboard}
                        searchText={searchText}
                        isLoading={isLoading}
                    />
                )}

                {dashboard?.id === 'billings' && (
                    <BillingsDashboard
                        invoices={filteredResults}
                        view={viewFilter}
                        dashboard={fetchedDashboard}
                        searchText={searchText}
                        isLoading={isLoading}
                    />
                )}

                {dashboard?.id === 'invoices' && (
                    <InvoicesDashboard
                        invoices={filteredResults}
                        dashboard={fetchedDashboard}
                        view={viewFilter}
                        searchText={searchText}
                        isLoading={isLoading}
                    />
                )}

                {dashboard?.id === 'inventory' && (
                    <InventoryDashboard
                        items={filteredResults}
                        dashboard={fetchedDashboard}
                        searchText={searchText}
                        isLoading={isLoading}
                    />
                )}

                {dashboard?.id === 'messages' && (
                    <MessagesDashboard
                        messages={filteredResults}
                        view={viewFilter}
                        dashboard={fetchedDashboard}
                        searchText={searchText}
                        isLoading={isLoading}
                    />
                )}

                {dashboard?.id === 'orders' && (
                    <OrdersDashboard
                        orders={filteredResults}
                        view={viewFilter}
                        dashboard={fetchedDashboard}
                        searchText={searchText}
                        isLoading={isLoading}
                    />
                )}
            </div>
        </div>
    )
}

export const DashboardDetailPage = usePSComponent(render, viewWillAppear)
